import { h } from 'preact';

import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { checkDimensionsAndAssignMedia } from 'utils/media';
import { isVideoFile } from 'utils/assets';

import Button from 'components/button/button';
import AddMediaButton from 'components/media/addMediaButton';
import { showFocalPointSelector } from 'components/media/focalPointSelector';
import { showMediaSelector } from 'components/media/mediaSelector';
import FieldBase from 'components/editor/settings/fieldBase';

import s from 'components/editor/settings/mediaField.sss';

export default class MediaField extends FieldBase {
	s = s;

	explicitDisabled = true;

	get displayValue() {
		const value = this.getValue();
		const inheritedValue = this.getInheritedValue();
		return value !== undefined ? value : inheritedValue;
	}

	constructor(props) {
		super(props);

		this.cacheKey = Date.now();
		this.cachedSrc = this.displayValue;

		this.setFocalPoint = this.setFocalPoint.bind(this);
		this.showMediaSelector = this.showMediaSelector.bind(this);
	}

	// Cache-busting token only changes when the asset does, so renders don't restart the download
	getDisplaySrc() {
		const src = this.displayValue;
		if (!src) return src;
		if (src !== this.cachedSrc) {
			this.cachedSrc = src;
			this.cacheKey = Date.now();
		}
		return `${src}${src.includes('?') ? '&' : '?'}v=${this.cacheKey}`;
	}

	getHelp() {
		if (this.props.help) return this.props.help;
		const { aspectRatio, recommendedDimensions } = this.props.assetData || {};
		return [
			this.props.helpTip,
			aspectRatio && `Aspect ratio: ${aspectRatio}`,
			recommendedDimensions && `Recommended dimensions: ${recommendedDimensions.replace(':', 'x')}px`,
		].filter(Boolean).join('\n');
	}

	async showMediaSelector() {
		if (!this.props.path) return;

		const newFile = await showMediaSelector({
			group: this.props.group,
			assetData: this.props.assetData,
			video: this.props.video,
			anyType: this.props.anyType,
		});

		if (newFile === 'CANCEL') return;
		if (!newFile?.s3_url) return;

		const assetData = this.props.assetData || {};
		assetData.group = this.props.group;

		if (this.props.path.split('/').shift() === 'movies') {
			assetData.type = 'movie';
		}

		if (this.props.path.split('/').shift() === 'sections') {
			assetData.type = 'section';
		}

		checkDimensionsAndAssignMedia(this.props.path, newFile, assetData);

		// Re-uploads can reuse the same URL, so force a new token
		this.cachedSrc = newFile.s3_url;
		this.cacheKey = Date.now();

		this.props.onInput?.(newFile);
	}

	async setFocalPoint() {
		if (!this.props.focalPointPath) return;
		const current = jsonPointer.get(Store.get().data, this.props.focalPointPath);
		const focalPoint = await showFocalPointSelector({
			focalPoint: current,
			imgSrc: this.displayValue,
		});
		if (!focalPoint || focalPoint === 'CANCEL') return;
		jsonPointer.set(Store.get().data, this.props.focalPointPath, focalPoint);
	}

	renderField() {
		const props = this.props;
		const src = this.displayValue;
		const video = props.video || (props.anyType && isVideoFile(src));
		const displaySrc = this.getDisplaySrc(); // Use cache-busted URL for display
		const aspectRatio = props.assetData?.aspectRatio;
		const styleAspectRatio = aspectRatio ? { aspectRatio: aspectRatio.replace(':', '/') } : {};
		const typename = props.anyType ? 'asset' : video ? 'video' : 'image';
		const showVideoMaxFileSize = video || props.anyType;

		return [
			src ? (
				<div class={s.edit} key="edit" style={styleAspectRatio}>
					<Button primary class={s.replace} onClick={this.showMediaSelector} disabled={this.readonly || this.disabled}>Replace {typename}</Button>
					{!video && props.focalPointPath && (<Button primary class={s.replace} onClick={this.setFocalPoint} disabled={this.readonly || this.disabled}>Set focal point</Button>)}
					{video ? <video src={displaySrc} controls /> : <img src={displaySrc} />}
				</div>
			) : (
				<div class={s.addImage} key="add">
					<AddMediaButton dark={props.dark} onClick={this.showMediaSelector} disabled={this.readonly || this.disabled} />
				</div>
			),
			!!props.anyType && <div class={s.mediaType} key="type">Image or video</div>,
			<div class={s.fileSize} key="size">Max file size {showVideoMaxFileSize ? '6MB' : '2MB'}</div>,
		];
	}
}